import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Phone, Mail, Target, History, Activity, Upload, Download, Tag, PackageX, PackageOpen, CheckSquare, Square } from "lucide-react";
import { api } from "../lib/api";
import { NEEDS_OUTCOME_CHANGED_EVENT } from "../lib/needsOutcome";
import { reliabilityStyle, formatReliabilityScore } from "../lib/reliability";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import ClientHistoryDrawer from "../components/ClientHistoryDrawer";
import ImportClientsDialog from "../components/ImportClientsDialog";
import { PageBand, OrangeButton } from "../components/layout/PageShell";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", goal: "", notes: "", rate: "", height_cm: "", weight_kg: "" };

export default function Clients() {
  const { readOnly } = useAuth();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [historyClient, setHistoryClient] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Bulk-select — Set of client_ids the trainer has ticked for a batch delete.
  // Reset whenever the roster reloads so a stale id can't survive into a
  // subsequent bulk-delete call.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/clients");
    setClients(data);
    // Any tick left over from a previous roster shouldn't survive a reload.
    setSelectedIds(new Set());
  }, []);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  }, []);

  useEffect(() => {
    load();
    // A session outcome changed anywhere (no-show, undo, complete) — the
    // reliability badges on this roster are derived from it, so reload.
    window.addEventListener(NEEDS_OUTCOME_CHANGED_EVENT, load);
    return () => window.removeEventListener(NEEDS_OUTCOME_CHANGED_EVENT, load);
  }, [load]);

  const handledNewParam = useRef(false);
  useEffect(() => {
    if (handledNewParam.current) return;
    if (params.get("new")) {
      handledNewParam.current = true;
      openCreate();
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams, openCreate]);

  const [emptyPackOnly, setEmptyPackOnly] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = clients;
    if (emptyPackOnly) base = base.filter((c) => c.pack_ever && Number(c.pack_remaining || 0) === 0);
    if (!q) return base;
    return base.filter((c) =>
      [c.name, c.email, c.phone, c.goal].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [clients, query, emptyPackOnly]);

  const emptyPackCount = useMemo(
    () => clients.filter((c) => c.pack_ever && Number(c.pack_remaining || 0) === 0).length,
    [clients]
  );

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, phone: c.phone, email: c.email, goal: c.goal, notes: c.notes,
      rate: c.rate == null ? "" : String(c.rate),
      height_cm: c.height_cm == null ? "" : String(c.height_cm),
      weight_kg: "",  // never prefilled — weight is history, edits never overwrite
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      goal: form.goal,
      notes: form.notes,
      rate: form.rate === "" || form.rate == null ? null : Number(form.rate),
      height_cm: form.height_cm === "" || form.height_cm == null ? null : Number(form.height_cm),
      // Only send weight_kg on create — on edit the backend ignores it and
      // we don't want to accidentally seed a history entry.
      weight_kg: editing || form.weight_kg === "" || form.weight_kg == null ? null : Number(form.weight_kg),
      // Carry the current pack-empty warning preference through the save so a
      // Save-after-toggle (or any edit while the flag is off) can't reset it
      // via the ClientIn default of `warn_pack_empty=True`. On create there
      // is no `editing`, so the field is simply not sent and Pydantic's
      // default (True) applies — the correct behaviour for a brand-new client.
      ...(editing ? { warn_pack_empty: editing.warn_pack_empty !== false } : {}),
    };
    try {
      if (editing) {
        await api.put(`/clients/${editing.client_id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/clients", payload);
        toast.success("Client added");
      }
      setOpen(false);
      await load();
    } catch {
      toast.error("Could not save client");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!c) return;
    const prev = clients;
    setClients((p) => p.filter((x) => x.client_id !== c.client_id));
    try {
      await api.delete(`/clients/${c.client_id}`);
      toast.success("Client deleted");
    } catch {
      setClients(prev);
      toast.error("Could not delete client");
    }
  };

  const confirmDelete = async () => {
    const c = pendingDelete;
    setPendingDelete(null);
    await remove(c);
  };

  const toggleSelect = (client_id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(client_id)) next.delete(client_id);
      else next.add(client_id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllVisible = () => {
    // "Select all" applies to the CURRENT filtered list only — the trainer
    // sees exactly what's about to be selected. This prevents nasty surprises
    // if a search filter is active.
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filtered.forEach((c) => next.add(c.client_id));
      return next;
    });
  };

  const selectedCount = selectedIds.size;
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.client_id));

  const confirmBulkDelete = async () => {
    if (selectedCount === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    try {
      const { data } = await api.post("/clients/bulk-delete", { client_ids: ids });
      const removed = data?.deleted ?? 0;
      toast.success(`Deleted ${removed} client${removed === 1 ? "" : "s"}`);
      setBulkConfirmOpen(false);
      await load();  // resets selectedIds too
    } catch {
      toast.error("Bulk delete failed. No clients were removed.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const exportCsv = () => {
    if (clients.length === 0) {
      toast.error("No clients to export");
      return;
    }
    const headers = ["name", "email", "phone", "notes", "fitness_goal", "weight_kg", "height_cm"];
    const escape = (v) => {
      const s = (v ?? "").toString();
      // Wrap in quotes and double any embedded quotes if the value contains a comma, quote or newline.
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      headers.join(","),
      ...clients.map((c) =>
        [c.name, c.email, c.phone, c.notes, c.goal, c.weight_kg ?? "", c.height_cm ?? ""].map(escape).join(","),
      ),
    ];
    // Prepend BOM so Excel opens UTF-8 correctly.
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `trainmate_clients_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${clients.length} client${clients.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <PageBand
        testid="clients-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              Client Roster
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Your clients
            </h1>
          </div>
        }
        right={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
            <OrangeButton
              onClick={openCreate}
              disabled={readOnly}
              title={readOnly ? "Your account is read only" : undefined}
              data-testid="add-client-button"
              className="order-1 sm:order-2 w-full sm:w-auto justify-center disabled:opacity-40"
              style={{ minHeight: 44 }}
            >
              <Plus className="w-4 h-4" /> Add Client
            </OrangeButton>
            <div className="order-2 sm:order-1 flex gap-2 min-w-0">
              <button
                onClick={exportCsv}
                disabled={clients.length === 0}
                data-testid="export-clients-btn"
                className="flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-full text-white text-sm font-semibold bg-white/15 backdrop-blur hover:bg-white/25 transition-colors disabled:opacity-40"
              >
                <Download className="w-4 h-4 shrink-0" /> <span className="truncate">Export CSV</span>
              </button>
              <button
                onClick={() => setImportOpen(true)}
                disabled={readOnly}
                title={readOnly ? "Your account is read only" : undefined}
                data-testid="import-clients-btn"
                className="flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-full text-white text-sm font-semibold bg-white/15 backdrop-blur hover:bg-white/25 transition-colors disabled:opacity-40"
              >
                <Upload className="w-4 h-4 shrink-0" /> <span className="truncate">Import CSV</span>
              </button>
            </div>
          </div>
        }
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B635C]" />
          <Input
            placeholder="Search by name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="clients-search"
            className="pl-9 bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623] placeholder:text-[#6B635C]"
          />
        </div>
        <button
          type="button"
          onClick={() => setEmptyPackOnly((v) => !v)}
          data-testid="filter-empty-pack"
          data-active={emptyPackOnly}
          disabled={emptyPackCount === 0 && !emptyPackOnly}
          className={
            "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded border transition disabled:opacity-40 disabled:cursor-not-allowed " +
            (emptyPackOnly
              ? "bg-[#F3D6D0] border-[#E0ADA4] text-[#A8372A]"
              : "bg-transparent border-[#E8E1D6] text-[#2A2623] hover:border-[#E7B9B2] hover:text-[#A8372A]")
          }
          title="Show only clients whose active package is empty — good follow-up-sale candidates."
        >
          <PackageX className="w-3.5 h-3.5" />
          Empty pack
          <span className="ml-1 px-1.5 py-0.5 rounded bg-[#E0D8CA] text-[10px]" data-testid="filter-empty-pack-count">
            {emptyPackCount}
          </span>
        </button>
      </div>

      {/* Bulk-select toolbar — only appears once the trainer ticks at least one card. */}
      {selectedCount > 0 && (
        <div
          className="flex items-center gap-3 flex-wrap rounded-md border border-[#E7B9B2] bg-[#F9E4DF] px-4 py-2.5"
          data-testid="bulk-toolbar"
        >
          <span className="text-sm font-semibold text-[#A8372A]" data-testid="bulk-selected-count">
            {selectedCount} selected
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={allVisibleSelected ? clearSelection : selectAllVisible}
            data-testid="bulk-select-all"
            className="text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 rounded border border-[#E8E1D6] text-[#2A2623] hover:text-[#2A2623] hover:border-[#C9BFAF]"
          >
            {allVisibleSelected ? "Clear all" : `Select all ${filtered.length} visible`}
          </button>
          <Button
            onClick={() => setBulkConfirmOpen(true)}
            data-testid="bulk-delete-btn"
            className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF] font-semibold"
          >
            <Trash2 className="w-4 h-4" /> Delete {selectedCount} selected
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
        {filtered.length === 0 ? (
          <div className="col-span-full tm-card p-10 text-center text-[#6B635C]">
            {clients.length === 0 ? "No clients yet. Add your first one." : "No clients match your search."}
          </div>
        ) : filtered.map((c) => {
          const isSelected = selectedIds.has(c.client_id);
          return (
          <div
            key={c.client_id}
            className={
              "tm-card p-5 transition-colors cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6B6B] " +
              (isSelected
                ? "border-[#D99E95] bg-[#F9E4DF]"
                : "hover:border-[#0F6B6B]/50")
            }
            data-testid={`client-card-${c.client_id}`}
            data-selected={isSelected}
            onClick={() => setHistoryClient(c)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setHistoryClient(c); } }}
          >
            <div className="flex items-start gap-3">
              {/* Select checkbox — click swallowed so the card's onClick (drawer)
                  doesn't fire. Tick is always visible so bulk-select is
                  discoverable without a hover state. */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleSelect(c.client_id); }}
                disabled={readOnly}
                data-testid={`client-select-${c.client_id}`}
                aria-label={isSelected ? "Deselect client" : "Select client"}
                aria-pressed={isSelected}
                className={
                  "shrink-0 h-8 w-8 flex items-center justify-center rounded border transition disabled:opacity-40 disabled:cursor-not-allowed " +
                  (isSelected
                    ? "border-[#A8372A] bg-[#F3D6D0] text-[#A8372A]"
                    : "border-[#E8E1D6] text-[#6B635C] hover:text-[#2A2623] hover:border-[#C9BFAF]")
                }
              >
                {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              </button>
              <div className="w-11 h-11 rounded-full bg-[#E8E1D6] flex items-center justify-center text-base font-bold text-[#0F6B6B]">
                {c.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold truncate max-w-full">{c.name}</div>
                  {(() => {
                    const rel = c.reliability;
                    const style = reliabilityStyle(rel?.tier);
                    return (
                      <span
                        data-testid={`reliability-badge-${c.client_id}`}
                        title={
                          rel?.tier === "new"
                            ? `New. Needs 3+ sessions (last 90 days)`
                            : `Reliability: ${formatReliabilityScore(rel)} · ${style.label} (last 90 days)`
                        }
                        className={`inline-flex items-center gap-1 shrink-0 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${style.chip}`}
                      >
                        <Activity className="w-3 h-3" />
                        {rel?.tier === "new" ? "New" : formatReliabilityScore(rel)}
                      </span>
                    );
                  })()}
                  <PackBadge client={c} />
                </div>
                {c.goal && <div className="text-xs text-[#0F6B6B] mt-0.5 flex items-start gap-1"><Target className="w-3 h-3 shrink-0 mt-0.5" />{c.goal}</div>}
              </div>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button size="icon" variant="ghost" onClick={() => setHistoryClient(c)} data-testid={`history-client-${c.client_id}`} className="h-8 w-8 text-[#6B635C] hover:text-[#0F6B6B]" title="View history">
                  <History className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} disabled={readOnly} title={readOnly ? "Your account is read only" : "Edit client"} data-testid={`edit-client-${c.client_id}`} className="h-8 w-8 text-[#6B635C] hover:text-[#2A2623] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setPendingDelete(c)} disabled={readOnly} title={readOnly ? "Your account is read only" : "Delete client"} data-testid={`delete-client-${c.client_id}`} className="h-8 w-8 text-[#6B635C] hover:text-[#A8372A] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-[#2A2623]">
              {c.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#6B635C]" /> {c.phone}</div>}
              {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-[#6B635C]" /> {c.email}</div>}
              {c.rate != null && (
                <div
                  data-testid={`client-rate-badge-${c.client_id}`}
                  className="flex items-center gap-2 text-[#0F6B6B]"
                  title="Custom price per class (incl. GST). Overrides your default rate."
                >
                  <Tag className="w-3.5 h-3.5" />
                  <span className="font-semibold">AUD ${Number(c.rate).toFixed(0)}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#6B635C] font-semibold">/class · incl. GST</span>
                </div>
              )}
            </div>
            {c.notes && <p className="text-xs text-[#6B635C] mt-3 line-clamp-2">{c.notes}</p>}
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">{editing ? "Edit client" : "Add client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" className="bg-[#FAF7F2] border-[#E8E1D6]" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" className="bg-[#FAF7F2] border-[#E8E1D6]" />
              </Field>
              <Field label="Email">
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" className="bg-[#FAF7F2] border-[#E8E1D6]" />
              </Field>
            </div>
            <Field label="Fitness goal">
              <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="client-goal" className="bg-[#FAF7F2] border-[#E8E1D6]" />
            </Field>
            <Field label="Price per class (AUD, incl. GST)">
              <Input
                type="number"
                min="0"
                step="1"
                inputMode="decimal"
                placeholder="Leave blank to use your default rate"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                data-testid="client-rate"
                className="bg-[#FAF7F2] border-[#E8E1D6]"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Height (cm)">
                <Input
                  type="number"
                  min="50"
                  max="260"
                  step="1"
                  inputMode="decimal"
                  placeholder="Optional"
                  value={form.height_cm}
                  onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                  data-testid="client-height"
                  className="bg-[#FAF7F2] border-[#E8E1D6]"
                />
              </Field>
              <Field label={editing ? "Weight (kg) — log new entries in Progress tab" : "Weight (kg) — starting entry"}>
                <Input
                  type="number"
                  min="10"
                  max="500"
                  step="0.1"
                  inputMode="decimal"
                  placeholder="Optional"
                  value={form.weight_kg}
                  onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                  data-testid="client-weight"
                  disabled={!!editing}
                  className="bg-[#FAF7F2] border-[#E8E1D6] disabled:opacity-50"
                />
              </Field>
            </div>
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="client-notes"
                rows={3}
                className="w-full rounded-md bg-[#FAF7F2] border border-[#E8E1D6] px-3 py-2 text-sm"
              />
            </Field>
            {editing && (
              <div
                className="flex items-start justify-between gap-3 rounded-md border border-[#E8E1D6] bg-[#FAF7F2] p-3"
                data-testid="warn-pack-empty-row"
              >
                <div className="text-sm">
                  <div className="text-[#2A2623] font-medium">Warn when pack is empty</div>
                  <div className="text-xs text-[#6B635C] mt-0.5">
                    Show the "Pack is empty" confirmation before booking or completing
                    a session for this client. Turn off for pay-per-session clients.
                  </div>
                </div>
                <Switch
                  checked={editing.warn_pack_empty !== false}
                  onCheckedChange={async (v) => {
                    const enabled = v === true;
                    try {
                      await api.post(`/clients/${editing.client_id}/warn-pack-empty`, { enabled });
                      // Reflect in the roster + the currently-editing snapshot
                      // so a toggle-flip-and-close doesn't leave stale state.
                      setClients((prev) => prev.map((c) => c.client_id === editing.client_id ? { ...c, warn_pack_empty: enabled } : c));
                      setEditing({ ...editing, warn_pack_empty: enabled });
                      toast.success(enabled ? "Pack-empty warning turned on" : "Pack-empty warning turned off");
                    } catch {
                      toast.error("Could not update warning setting");
                    }
                  }}
                  data-testid="warn-pack-empty-toggle"
                  className="data-[state=checked]:bg-[#0F6B6B]"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]" data-testid="client-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold" data-testid="client-save">
              {saving ? "Saving..." : "Save client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="client-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Delete client?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              Are you sure you want to delete{pendingDelete ? ` ${pendingDelete.name}` : " this client"}? Past invoices will be kept. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="client-delete-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="client-delete-confirm"
              className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirmOpen} onOpenChange={(o) => { if (!o) setBulkConfirmOpen(false); }}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
              Delete {selectedCount} client{selectedCount === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              This removes {selectedCount} client{selectedCount === 1 ? "" : "s"} from your roster. Their past sessions, invoices, and weight history stay put with a name snapshot so your records and exports still work. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="bulk-delete-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
              disabled={bulkDeleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDelete}
              data-testid="bulk-delete-confirm"
              disabled={bulkDeleting}
              className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF] disabled:opacity-50"
            >
              {bulkDeleting ? "Deleting…" : `Delete ${selectedCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ClientHistoryDrawer
        client={historyClient}
        open={!!historyClient}
        onOpenChange={(o) => !o && setHistoryClient(null)}
      />

      <ImportClientsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={load}
      />
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#6B635C] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// Small, quiet pack-status badge shown next to reliability. Only renders when
// the client has ever had a package — pay-per-session clients stay uncluttered.
function PackBadge({ client }) {
  if (!client?.pack_ever) return null;
  const remaining = Number(client.pack_remaining || 0);
  let style;
  let label;
  let Icon;
  let title;
  if (remaining === 0) {
    Icon = PackageX;
    label = "0 in pack";
    style = "border-[#F0C9C1] bg-[#F9E4DF] text-[#A8372A]";
    title = "This client's active package is empty. New sessions won't be covered by a pack.";
  } else if (remaining <= 2) {
    Icon = PackageOpen;
    label = `${remaining} left`;
    style = "border-[#ECDCA6] bg-[#FBF4DD] text-[#7A5E18]";
    title = `Only ${remaining} session${remaining === 1 ? "" : "s"} left in this client's active package.`;
  } else {
    return null;
  }
  return (
    <span
      data-testid={`pack-badge-${client.client_id}`}
      data-pack-state={remaining === 0 ? "empty" : "low"}
      title={title}
      className={`inline-flex items-center gap-1 shrink-0 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${style}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}
